import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Save } from 'lucide-react';
import { Input } from './ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/portal`;
const eur = (c) => `${((c || 0) / 100).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const box = 'bg-[#0B0D10] border border-[#1F242C] p-6';
const th = 'py-2 pr-3 font-normal';
const margin = (c) => (c < 0 ? 'text-[#E37A72]' : 'text-[#5FC18F]');
const NAMES = { openapi: 'OpenAPI · Negatività', creditsafe: 'Creditsafe · Report e documenti', sister: 'SISTER · Ispezioni ipotecarie' };

function Products({ p, rows, units, setUnits }) {
  return (
    <div className="overflow-x-auto"><table className="w-full text-sm">
      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#5A5F67] border-b border-[#1F242C]">
        <th className={th}>Prodotto</th><th className={th}>Utilizzi</th><th className={th}>Incassato dai clienti</th><th className={th}>Costo unitario fornitore (€)</th><th className={th}>Costo fornitore</th><th className="py-2 font-normal">Margine</th>
      </tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.key} className="border-b border-[#1F242C]" data-testid={`pc-${p}-${r.key}`}>
          <td className="py-2 pr-3 text-white">{r.label}</td>
          <td className="py-2 pr-3 font-mono text-xs text-[#BFBFBF]">{r.count}{r.included ? <span className="text-[#5A5F67]"> (di cui {r.included} inclusi)</span> : ''}</td>
          <td className="py-2 pr-3 font-mono text-xs text-[#BFBFBF]">{eur(r.revenue_cents)}</td>
          <td className="py-2 pr-3"><Input type="number" min={0} step="0.01" value={units[r.key]} onChange={(e) => setUnits(r.key, e.target.value)} className="h-8 w-24 bg-black border-[#1F242C] text-white" data-testid={`pc-unit-${p}-${r.key}`} /></td>
          <td className="py-2 pr-3 font-mono text-xs text-[#BFBFBF]">{eur(r.cost_cents)}</td>
          <td className={`py-2 font-mono text-xs ${margin(r.margin_cents)}`}>{eur(r.margin_cents)}</td>
        </tr>
      ))}</tbody>
    </table></div>
  );
}

function Clients({ p, clients }) {
  if (!clients.length) return <p className="text-xs text-[#5A5F67] mt-4">Nessun utilizzo nel mese.</p>;
  return (
    <div className="overflow-x-auto mt-5"><p className="text-[11px] uppercase tracking-wider text-[#5A5F67] mb-1">Per cliente</p><table className="w-full text-sm">
      <tbody>{clients.map((c) => (
        <tr key={c.id} className="border-b border-[#1F242C]" data-testid={`pc-${p}-client-${c.id}`}>
          <td className="py-2 pr-3"><p className="text-white">{c.company}</p><p className="text-xs text-[#5A5F67]">{c.email}</p></td>
          <td className="py-2 pr-3 font-mono text-xs text-[#BFBFBF]">{c.count} utilizzi</td>
          <td className="py-2 pr-3 font-mono text-xs text-[#BFBFBF]">incassato {eur(c.revenue_cents)}</td>
          <td className="py-2 pr-3 font-mono text-xs text-[#BFBFBF]">costo {eur(c.cost_cents)}</td>
          <td className={`py-2 font-mono text-xs ${margin(c.margin_cents)}`}>margine {eur(c.margin_cents)}</td>
        </tr>
      ))}</tbody>
    </table></div>
  );
}

function Account({ p, d }) {
  if (p === 'sister') return <p className="text-xs text-[#5A5F67] mb-4">Costi addebitati sulla convenzione SISTER: elenco sintetico e singole note aperte.</p>;
  if (p === 'openapi') {
    if (d.openapi_error) return <p className="text-xs text-[#E37A72] mb-4">{d.openapi_error}</p>;
    const a = d.openapi_account;
    if (!a) return <p className="text-xs text-[#5A5F67] mb-4">OpenAPI non configurato in questo ambiente.</p>;
    return (
      <div className="mb-4 text-xs text-[#BFBFBF]" data-testid="pc-openapi-account">
        <p>Credito residuo sull'account OpenAPI: <span className="font-mono text-white text-sm" data-testid="pc-openapi-credit">{a.credit.toLocaleString('it-IT')}</span>{a.sandbox && <span className="text-[#D4AF37]"> · ambiente di prova (sandbox)</span>}</p>
        <p className="text-[#5A5F67] mt-1">Chiamate totali sull'account: {a.counters.map((c) => `${c.label} ${c.count} (a pagamento ${c.paid})`).join(' · ') || '—'}</p>
      </div>
    );
  }
  const lines = (d.creditsafe_contract?.lines || []).filter((l) => l.paid && (l.used || l.country === 'IT'));
  return (
    <div className="mb-4 text-xs text-[#BFBFBF]" data-testid="pc-creditsafe-contract">
      {lines.length ? lines.map((l, i) => <p key={i}>Contratto {[l.country, l.template || l.product].filter(Boolean).join(' · ')}: residui <span className="font-mono text-white">{l.paid - l.used} / {l.paid}</span> ({l.percent_left}%)</p>)
        : <p className="text-[#5A5F67]">Contratto non ancora letto: usa «Verifica ora» in Plafond e crediti.</p>}
    </div>
  );
}

function OpenapiAlert({ d, headers }) {
  const [v, setV] = useState(String(d.openapi_alert?.threshold ?? 20));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/provider-costs/openapi-alert`, { threshold: Number(String(v).replace(',', '.')) }, { headers });
      const r = (await axios.post(`${API}/provider-costs/openapi-alert/check`, {}, { headers })).data;
      toast.success(r.email_sent ? 'Soglia salvata: credito sotto soglia, email inviata' : 'Soglia salvata');
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Soglia non valida'); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="pc-openapi-alert">
      <span className="text-xs text-[#BFBFBF]">Email all'admin quando il credito scende sotto</span>
      <Input value={v} onChange={(e) => setV(e.target.value)} className="h-8 w-24 bg-black border-[#1F242C] text-white" data-testid="pc-openapi-alert-threshold" />
      <button onClick={save} disabled={busy} className="h-8 px-3 text-xs flex items-center gap-1 bg-[#D4AF37] text-black rounded-sm" data-testid="pc-openapi-alert-save">{busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}Salva soglia</button>
      <span className="text-[11px] text-[#5A5F67]">Controllo automatico ogni giorno; l'avviso parte una volta e si riattiva dopo la ricarica.{d.openapi_alert?.sent ? ' Avviso già inviato.' : ''}</span>
    </div>
  );
}

export default function AdminProviderCostsPanel({ headers }) {
  const [month, setMonth] = useState(new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' }).slice(0, 7));
  const [d, setD] = useState(null);
  const [units, setUnitsState] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setD(null);
    axios.get(`${API}/provider-costs`, { headers, params: { month } }).then((r) => {
      setD(r.data);
      setUnitsState(Object.fromEntries(Object.entries(r.data.providers).map(([p, v]) => [p, Object.fromEntries(v.products.map((x) => [x.key, (x.unit_cost_cents / 100).toString()]))])));
    }).catch(() => toast.error('Resoconto non caricato'));
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(units).map(([p, v]) => [p, Object.fromEntries(Object.entries(v).map(([k, x]) => [k, Math.round(Number(x || 0) * 100)]))]));
      await axios.put(`${API}/provider-costs/units`, body, { headers });
      toast.success('Costi unitari salvati'); load();
    } catch { toast.error('Costi non validi'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="admin-provider-costs">
      <div className="flex flex-wrap items-center gap-3">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-44 bg-black border-[#1F242C] text-white" data-testid="pc-month" />
        <button onClick={load} className="h-9 px-3 text-xs flex items-center gap-1 border border-[#1F242C] text-[#BFBFBF] hover:text-white rounded-sm" data-testid="pc-refresh"><RefreshCw size={12} />Aggiorna</button>
        <button onClick={save} disabled={busy || !units} className="h-9 px-3 text-xs flex items-center gap-1 bg-[#D4AF37] text-black rounded-sm" data-testid="pc-save-units">{busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}Salva costi unitari</button>
        <p className="w-full text-xs text-[#5A5F67]">Utilizzi del mese letti da BOND, incassi dai plafond dei clienti e costo fornitore calcolato con il costo unitario che inserisci (dal tuo contratto). Le negatività incluse nel reputational check non hanno incasso diretto.</p>
      </div>
      {!d || !units ? <Loader2 className="animate-spin text-[#D4AF37]" /> : Object.entries(d.providers).map(([p, v]) => (
        <div key={p} className={box} data-testid={`pc-provider-${p}`}>
          <div className="flex flex-wrap justify-between items-baseline gap-2 mb-3">
            <h3 className="font-heading text-sm uppercase tracking-widest text-[#BFBFBF]">{NAMES[p]}</h3>
            <p className="text-xs font-mono text-[#BFBFBF]" data-testid={`pc-${p}-totals`}>{v.totals.count} utilizzi · incassato {eur(v.totals.revenue_cents)} · costo {eur(v.totals.cost_cents)} · <span className={margin(v.totals.margin_cents)}>margine {eur(v.totals.margin_cents)}</span></p>
          </div>
          <Account p={p} d={d} />
          {p === 'openapi' && d.openapi_account && <OpenapiAlert d={d} headers={headers} />}
          <Products p={p} rows={v.products} units={units[p]} setUnits={(k, x) => setUnitsState((u) => ({ ...u, [p]: { ...u[p], [k]: x } }))} />
          <Clients p={p} clients={v.clients} />
        </div>
      ))}
    </div>
  );
}
